import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV_ITEMS = [
  {
    label: "Services",
    to: "/services",
    children: [
      { to: "/services", label: "All Practices" },
      { to: "/ai-services", label: "AI Services" },
      { to: "/services/ai-strategy", label: "AI Strategy" },
      { to: "/services/digital-transformation", label: "Digital Transformation" },
      { to: "/services/operational-excellence", label: "Operational Excellence" },
      { to: "/services/compliance-governance", label: "Compliance & Governance" },
    ],
  },
  {
    label: "Platforms",
    to: "/platforms",
    children: [
      { to: "/platforms", label: "Portfolio Overview" },
      { to: "/platforms/alchamis-plm", label: "Alchamis PLM" },
      { to: "/platforms/alchamis-crm", label: "Alchamis CRM" },
      { to: "/platforms/alouvya", label: "Alouvya" },
      { to: "/platforms/datendog", label: "Date'n'Dog" },
    ],
  },
  {
    label: "Industries",
    to: "/industries",
    children: [
      { to: "/industries", label: "All Industries" },
      { to: "/industries/financial-services", label: "Financial Services" },
      { to: "/industries/healthcare", label: "Healthcare" },
      { to: "/industries/manufacturing", label: "Manufacturing" },
      { to: "/industries/aerospace-defense", label: "Aerospace & Defense" },
      { to: "/industries/technology", label: "Technology" },
    ],
  },
  { label: "AI Advisor", to: "/ai-advisor" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

function NavItem({ item }) {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase font-medium transition-all duration-300 ${
            isActive ? "text-gold" : "text-white/75 hover:text-gold"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className="relative"
    >
      <NavLink
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `px-3 py-2 inline-flex items-center gap-1.5 text-[0.7rem] tracking-[0.18em] uppercase font-medium transition-all duration-300 ${
            isActive ? "text-gold" : "text-white/75 hover:text-gold"
          }`
        }
      >
        {item.label} <ChevronDown size={12} />
      </NavLink>
      {open && (
        <div className="absolute left-0 top-full pt-3">
          <div className="min-w-[260px] bg-[#001B54] border border-gold/25 shadow-[0_24px_60px_-15px_rgba(0,0,0,0.6)] py-2">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={`nav-sub-${c.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `block px-5 py-2.5 text-xs tracking-wide uppercase transition-colors ${
                    isActive ? "text-gold bg-white/5" : "text-white/75 hover:text-gold hover:bg-white/5"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    queueMicrotask(() => setOpen(false));
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#001B54] border-b border-gold/30 shadow-[0_8px_30px_-15px_rgba(0,0,0,0.5)]"
          : "bg-[#001B54]/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="navbar-logo" aria-label="AlchamisGroup — Home">
          <img
            src="/alchamis-logo.svg"
            alt="AlchamisGroup"
            className="h-16 w-auto select-none transition-all duration-500 hover:[filter:drop-shadow(0_0_24px_rgba(212,175,55,0.5))]"
            draggable="false"
            decoding="async"
            loading="eager"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavItem key={item.label} item={item} />
          ))}
          <Link
            to="/ai-advisor"
            data-testid="nav-cta-engage"
            className="luxury-button-gold ml-3"
          >
            Engage
          </Link>
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          className="xl:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[#001B54] border-t border-gold/25 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-2">
            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="border-b border-white/10 pb-3">
                <NavLink
                  to={item.to}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-2 text-sm tracking-[0.18em] uppercase ${
                      isActive ? "text-gold" : "text-white/85"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
                {item.children && (
                  <div className="mt-1 pl-4 space-y-2">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        className={({ isActive }) =>
                          `block text-xs ${isActive ? "text-gold" : "text-white/55"}`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/ai-advisor" className="luxury-button-gold mt-3 justify-center" data-testid="nav-mobile-cta">
              Engage Advisor
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
